import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';

import { RenderLogo } from '../../../../app/appHeader/helpers';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetDateFNSLocale } from '../../../../locales/helpers';
import { usePreferences } from '../../../../react-query/usePreferences';
import {
  black,
  blueUltramarine055,
  fontFamiliesNeueHaasGroteskDisplayPro,
  fontWeightsNeueHaasGroteskDisplayProRegular,
} from '../../../../theme/videa-style-tokens';
import { useGetCountry } from '../../../../utils/internationalization/useGetCountry';
import { previewModeAtom } from '../../multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '../../types';
import {
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
  selectedTeethPreviewAtom,
} from '../atoms';

import { useFindingsHidden } from './hooks/useFindingsHidden';
import { PatientDownloadableFindings } from './PatientDownloadableFindings';
import { PatientDownloadableImage } from './PatientDownloadableImage';
import { TreatmentsText } from './TreatmentOptions';

export const DownloadableStyles = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 60em;
  padding: 1em;
  overflow-y: scroll;

  &.videa-mobile-preview {
    width: 100%;
    padding: 2em;
    font-size: 14px;

    .patient-downloadable-logo {
      margin-top: 0;

      svg {
        height: 48px;
        width: auto;
      }
    }

    .practice-name {
      font-size: 1.5em;
      margin-top: 0.5em;
    }

    .ai-analysis {
      font-size: 3em;
    }

    .description {
      font-size: 1em;
    }

    .visit-date {
      font-size: 1em;
      margin-top: 1em;
    }

    .images-and-findings {
      display: flex;
      flex-direction: column;

      .images-box {
        width: 100%;
      }

      .videa-findings {
        width: 100%;
      }
    }
  }

  .navy-runner {
    background-color: #2f3e67;
    width: 100%;
    height: 24px;
  }
  .patient-downloadable-logo {
    margin-top: 1.75em;
    svg {
      height: 72px;
      width: auto;
    }
  }
  .practice-name {
    font-family: ${fontFamiliesNeueHaasGroteskDisplayPro};
    font-size: 1.5em;
    font-weight: 700;
    margin-top: 1.5em;
  }
  .ai-analysis {
    font-family: ${fontFamiliesNeueHaasGroteskDisplayPro};
    font-size: 4em;
    font-weight: 700;
    margin-top: 0.5em;
  }
  .description {
    font-family: ${fontFamiliesNeueHaasGroteskDisplayPro};
    font-size: 1em;
    font-weight: ${fontWeightsNeueHaasGroteskDisplayProRegular};
  }
  .visit-date {
    font-family: ${fontFamiliesNeueHaasGroteskDisplayPro};
    font-size: 1em;
    font-weight: 700;
    margin-top: 1.5em;
  }
  .findings-disclaimer {
    font-size: 0.75em;
    padding: 2em 0 0 0;
  }
  .images-and-findings {
    break-inside: avoid;
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 1em;
    row-gap: 1em;
    margin-top: 1em;

    .images-box-container {
      display: flex;
      flex-direction: column;
      gap: 1em;
    }
    .images-box {
      height: 25em;
      width: 30em;
    }
    .videa-findings {
      display: flex;
      flex-direction: column;
      width: 20em;
    }
  }

  .treatment-group {
    break-before: always !important;
    break-inside: avoid !important;
    margin-top: 2em;
    background-color: ${blueUltramarine055};
    border-top: 1px solid ${black};
    padding: 2em;

    .treatment-option {
      // Using padding-top instead of gap or margin-top ensures that, when
      // printing and a treatment option is at the top of a new page, the top of
      // the treatment image is not flush against the top of the blue box.
      padding-top: 1.5em;
      break-inside: avoid !important;

      &.desktop-preview {
        align-items: flex-start;

        &:nth-of-type(even) {
          flex-direction: row-reverse;
        }
      }
    }

    p {
      font-weight: 500;
      font-size: 1em;
    }
    .notes {
      font-size: 1em;
      white-space: pre-wrap;
    }
  }
`;

export const PatientDownloadable = ({
  currentImage,
  selectedPhoto,
  practiceName,
  patientName,
  visitDate,
}: {
  currentImage: ImageObject;
  selectedPhoto?: ImageObject;
  patientName?: string;
  visitDate?: Date;
  practiceName: string;
}) => {
  const { t } = useLingui();
  const [notes] = useAtom(patientDownloadableNotesAtom);

  // Fetch props from atoms and hooks
  const selectedTreatments = useAtomValue(
    patientDownloadableSelectedTreatmentsAtom,
  );
  const previewMode = useAtomValue(previewModeAtom);
  const dateFNSLocale = useGetDateFNSLocale();
  const isFindingsHidden = useFindingsHidden();
  const isCanadianPractice = useGetCountry() === 'CA';
  const selectedTeethPreview = useAtomValue(selectedTeethPreviewAtom);

  const getPageMargins = () => {
    return `@page { margin: 1in 0; } @page :first { margin: 0; }`;
  };

  return (
    <>
      <style>{getPageMargins()}</style>
      <div className="navy-runner" />
      <div className="patient-downloadable-logo">
        <RenderLogo />
      </div>
      <VideaTypography className="practice-name">
        {practiceName}
      </VideaTypography>
      <VideaTypography className="ai-analysis">
        <Trans>AI Analysis</Trans>
      </VideaTypography>
      <VideaTypography className="description">
        {isCanadianPractice ? (
          <Trans>
            Your dental practice has analyzed your x-rays with clinically
            validated AI algorithms from VideaAI. This is a copy of the findings
            for your records. These findings are not direct diagnoses.
          </Trans>
        ) : (
          <Trans>
            Your dental practice has analyzed your x-rays with FDA-cleared AI
            algorithms from VideaAI. This is a copy of the findings for your
            records. These findings are not direct diagnoses.
          </Trans>
        )}
      </VideaTypography>
      {visitDate && (
        <VideaTypography className="visit-date">
          {format(new Date(visitDate), 'MMM. dd, yyyy', {
            locale: dateFNSLocale,
          })}
        </VideaTypography>
      )}

      {/* Main image */}
      <div className="images-and-findings">
        <VideaTypography>
          {previewMode === 'print' && patientName
            ? String(patientName).toUpperCase()
            : null}
        </VideaTypography>
        {/* empty div for grid */}
        {<div></div>}
        <div className="images-box-container">
          <div className="images-box">
            <PatientDownloadableImage activeImage={currentImage} />
          </div>
        </div>
        <div className="videa-findings">
          <PatientDownloadableFindings currentImage={currentImage} />
        </div>
      </div>

      {/* Selected photo (if any) */}
      {!!selectedPhoto && (
        <div className="images-and-findings">
          <div className="images-box-container">
            <VideaTypography>
              {currentImage.detectedImageType === 'BITEWING' ||
              currentImage.detectedImageType === 'PERIAPICAL'
                ? t`AI ANALYZED IMAGE`
                : t`IMAGE`}
            </VideaTypography>
            <div className="images-box">
              <PatientDownloadableImage
                activeImage={selectedPhoto}
                isSelectedPhoto
              />
            </div>
          </div>
          <PatientDownloadableFindings currentImage={selectedPhoto} />
        </div>
      )}

      {(!!isFindingsHidden || selectedTeethPreview.length > 0) && (
        <VideaTypography className="findings-disclaimer">
          <Trans>
            * Note: some analyzed findings may not be displayed in image and
            summary above
          </Trans>
        </VideaTypography>
      )}
      {selectedTreatments && !!selectedTreatments.length && (
        <div className="treatment-group">
          <VideaTypography>
            <Trans>TREATMENT</Trans>
          </VideaTypography>
          {selectedTreatments.map((treatment) => {
            return (
              <TreatmentsText
                key={treatment}
                showDiagram={true}
                treatment={treatment}
              />
            );
          })}
        </div>
      )}

      {!!notes?.length && (
        <div className="treatment-group">
          <VideaTypography>
            <Trans>ADDITIONAL NOTES</Trans>
          </VideaTypography>
          <VideaTypography className="notes">{notes}</VideaTypography>
        </div>
      )}
    </>
  );
};
